/**
 * components/ProjectFilterPills.jsx — v2.87 Phase 2 (Sprint B)
 * -------------------------------------------------------------
 * Project-specific filter strip for /projects. Visually shares the same
 * pill-row language as FilterPills (listing filters), BUT the filter
 * vocabulary, query keys, and option lists are project-specific and
 * decoupled.  Listing form ↔ project form must NEVER merge logic.
 *
 *   Row 1: [Search input.....................................]
 *   Row 2: [All] [Ready] [Under Construction] [Launching Soon]   ← status chips
 *   Row 3: [Location ▾] [Builder ▾] [BHK ▾] [Price ▾]
 *          [Possession window ▾] [☑ RERA available] [Sort ▾]    ← filter pills
 *
 * Backend: GET /api/projects?paginated=true&...
 *   Params: q, location, builder_id, bhk, min_price, max_price,
 *           possession_window, rera, sort, status, page, page_size.
 *
 * URL ⇄ state contract:
 *   • Parent (ProjectsPage) owns filter state + URL sync.
 *   • This component is presentational — calls setFilters(patch).
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "./ui/sheet";
import {
  ChevronDown, X, Search, ShieldCheck, ArrowUpDown, Check, Clock, MapPin, Hammer,
} from "lucide-react";

// ─── option vocabulary ─────────────────────────────────────────────
// Phase I-B.1 — Status / BHK / Possession options are NO LONGER hard-coded
// here. They come from /api/project-schema/public via ProjectsPage.jsx
// (`statusOptions`, `bhkOptions`, `possessionOptions` props). The icon map
// below decorates rendered status chips with the same icon vocabulary the
// previous hard-coded array used.

const STATUS_ICON_MAP = {
  ready_to_move: Check,
  under_construction: Hammer,
  launching_soon: Clock,
};

// Projects are sold by builder => Sale-style budget buckets only.
const PRICE_PRESETS = [
  { label: "Up to ₹50 L",     range: [0, 5000000] },
  { label: "₹50 L – ₹1 Cr",   range: [5000000, 10000000] },
  { label: "₹1 – 2 Cr",       range: [10000000, 20000000] },
  { label: "₹2 – 5 Cr",       range: [20000000, 50000000] },
  { label: "₹5 Cr+",          range: [50000000, 999999999] },
];

const SORT_OPTIONS = [
  { value: "latest",              label: "Latest first" },
  { value: "price_asc",           label: "Price: Low → High" },
  { value: "price_desc",          label: "Price: High → Low" },
  { value: "possession_soonest",  label: "Possession: Soonest" },
];
const DEFAULT_SORT = "latest";

// ─── helpers ───────────────────────────────────────────────────────
// v2.87 Phase 3a — multi-select via comma-separated strings (backend-friendly).
const csvToArr = (csv) => String(csv || "").split(",").map((x) => x.trim()).filter(Boolean);
const arrToCsv = (arr) => (arr || []).filter(Boolean).join(",");
const csvLabel = (csv) => {
  const arr = csvToArr(csv);
  if (arr.length === 0) return "";
  if (arr.length === 1) return arr[0];
  return `${arr[0]} +${arr.length - 1}`;
};
const csvIncludes = (csv, v) => csvToArr(csv).includes(v);
const csvToggle = (csv, v) => {
  const arr = csvToArr(csv);
  return arrToCsv(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
};

const fmtINR = (n) => {
  const num = Number(n) || 0;
  if (num >= 10000000) return `₹${(num / 10000000).toFixed(num % 10000000 === 0 ? 0 : 1)} Cr`;
  if (num >= 100000)   return `₹${(num / 100000).toFixed(num % 100000 === 0 ? 0 : 1)} L`;
  return `₹${num}`;
};

const statusTone = (key, active) => {
  if (!active) return "bg-white text-slate-700 border-slate-200 hover:border-[#0055FF]/40 hover:bg-slate-50";
  if (key === "ready_to_move")      return "bg-[#15803D] text-white border-[#15803D] shadow-sm";
  if (key === "under_construction") return "bg-[#B45309] text-white border-[#B45309] shadow-sm";
  if (key === "launching_soon")     return "bg-[#0055FF] text-white border-[#0055FF] shadow-sm";
  return "bg-[#09090B] text-white border-[#09090B] shadow-sm";
};

// ─── Main component ─────────────────────────────────────────────────
export default function ProjectFilterPills({
  filters,
  setFilters,
  onReset,
  builderOptions = [],          // [{id, name}] derived from current project list (parent provides)
  locationOptions = [],         // string list of free-text locations (parent provides)
  amenityOptions = [],          // v2.87 Phase 3b ext — list of amenity strings derived from facets
  // Phase I-B.1 — dynamic schema-driven options from /api/project-schema/public
  statusOptions = [],           // [{ key, label }] excluding the synthetic "All"
  bhkOptions = [],              // ["1 BHK", "2 BHK", ...]
  possessionOptions = [],       // [{ value, label }]
  testidPrefix = "pfp",
}) {
  const [activePop, setActivePop] = useState(null);
  const [searchVal, setSearchVal] = useState(filters.q || "");
  const rootRef = useRef(null);

  // Outside-click dismiss (desktop only).
  useEffect(() => {
    if (!activePop) return;
    if (typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setActivePop(null);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [activePop]);

  // Debounce search input → filters.q
  useEffect(() => {
    const t = setTimeout(() => {
      if ((filters.q || "") !== searchVal) {
        setFilters((p) => ({ ...p, q: searchVal }));
      }
    }, 300);
    return () => clearTimeout(t);
  }, [searchVal]); // eslint-disable-line react-hooks/exhaustive-deps

  const setPatch = (patch) => setFilters((prev) => ({ ...prev, ...patch }));
  const closePop = () => setActivePop(null);

  // Derived labels
  const pricePreset = useMemo(() => {
    if (!filters.priceRange) return null;
    if (filters.priceRange[0] === 0 && filters.priceRange[1] >= 999999999) return null;
    return PRICE_PRESETS.find((p) =>
      p.range[0] === filters.priceRange[0] && p.range[1] === filters.priceRange[1]
    ) || null;
  }, [filters.priceRange]);
  const priceLabel = pricePreset
    ? pricePreset.label
    : (filters.priceRange && !(filters.priceRange[0] === 0 && filters.priceRange[1] >= 999999999)
        ? `${fmtINR(filters.priceRange[0])} – ${fmtINR(filters.priceRange[1])}`
        : "");

  const possessionLabel = possessionOptions.find((o) => o.value === filters.possession_window)?.label || "";
  const sortActive = (filters.sort || DEFAULT_SORT) !== DEFAULT_SORT;
  const sortLabel = SORT_OPTIONS.find((o) => o.value === (filters.sort || DEFAULT_SORT))?.label || "Latest first";

  // Count active filters for the Reset link visibility.
  const activeCount = (() => {
    let n = 0;
    if (filters.status)              n++;
    if (filters.q)                   n++;
    if (filters.location)            n++;
    if (filters.builder_id)          n++;
    if (filters.bhk)                 n++;
    if (priceLabel)                  n++;
    if (filters.possession_window)   n++;
    if (filters.rera)                n++;
    if (filters.amenities)           n++;
    if (sortActive)                  n++;
    return n;
  })();

  return (
    <div ref={rootRef} className="relative w-full" data-testid={`${testidPrefix}-root`}>
      {/* ===== Row 1 — Search ===== */}
      <div className="pb-2" data-testid={`${testidPrefix}-search-row`}>
        <div className="relative">
          <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <Input
            value={searchVal}
            onChange={(e) => setSearchVal(e.target.value)}
            placeholder="Search projects by name, builder, location…"
            className="rounded-full h-10 pl-9 pr-10 border-slate-200 bg-white focus:border-[#0055FF]"
            data-testid={`${testidPrefix}-search`}
          />
          {searchVal && (
            <button
              type="button"
              onClick={() => setSearchVal("")}
              aria-label="Clear search"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
              data-testid={`${testidPrefix}-search-clear`}
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* ===== Row 2 — Status chips (multi-select) ===== */}
      <div
        className="flex items-center gap-2 overflow-x-auto md:overflow-visible no-scrollbar pb-2"
        data-testid={`${testidPrefix}-status-row`}
      >
        {/* "All" — clears the status filter. Always rendered first. */}
        {(() => {
          const active = !filters.status;
          return (
            <button
              key="all"
              type="button"
              onClick={() => { setPatch({ status: "" }); closePop(); }}
              data-testid={`${testidPrefix}-status-all`}
              aria-pressed={active}
              className={`shrink-0 inline-flex items-center gap-1.5 h-9 px-4 rounded-full border text-[12.5px] font-semibold transition-all duration-200
                          ${statusTone("", active)}`}
            >
              All
            </button>
          );
        })()}
        {statusOptions.map(({ key, label }) => {
          const Icon = STATUS_ICON_MAP[key] || null;
          const active = csvIncludes(filters.status, key);
          return (
            <button
              key={key}
              type="button"
              onClick={() => { setPatch({ status: csvToggle(filters.status, key) }); }}
              data-testid={`${testidPrefix}-status-${key}`}
              aria-pressed={active}
              className={`shrink-0 inline-flex items-center gap-1.5 h-9 px-4 rounded-full border text-[12.5px] font-semibold transition-all duration-200
                          ${statusTone(key, active)}`}
            >
              {Icon && <Icon size={12} strokeWidth={2.4} />}
              {label}
            </button>
          );
        })}
        {activeCount > 0 && (
          <button
            type="button"
            onClick={() => { setSearchVal(""); onReset?.(); closePop(); }}
            data-testid={`${testidPrefix}-reset`}
            className="ml-auto shrink-0 inline-flex items-center gap-1 h-9 px-3 text-[12px] text-slate-500 hover:text-slate-900 transition-colors"
            aria-label="Reset filters"
          >
            <X size={12} /> Reset all
          </button>
        )}
      </div>

      {/* ===== Row 3 — Filter pills ===== */}
      <div
        className="flex items-center gap-2 overflow-x-auto md:overflow-visible no-scrollbar pb-2"
        data-testid={`${testidPrefix}-row`}
      >
        <Pill
          label="Location"
          value={filters.location || ""}
          icon={<MapPin size={11} />}
          active={activePop === "location"}
          onClick={() => setActivePop((p) => (p === "location" ? null : "location"))}
          testid={`${testidPrefix}-location`}
        />
        <Pill
          label="Builder"
          value={csvLabel(filters.builder_id) && (builderOptions.find((b) => b.id === csvToArr(filters.builder_id)[0])?.name || csvLabel(filters.builder_id))}
          active={activePop === "builder"}
          onClick={() => setActivePop((p) => (p === "builder" ? null : "builder"))}
          testid={`${testidPrefix}-builder`}
        />
        <Pill
          label="BHK / Unit"
          value={csvLabel(filters.bhk)}
          active={activePop === "bhk"}
          onClick={() => setActivePop((p) => (p === "bhk" ? null : "bhk"))}
          testid={`${testidPrefix}-bhk`}
        />
        <Pill
          label="Starting price"
          value={priceLabel}
          active={activePop === "price"}
          onClick={() => setActivePop((p) => (p === "price" ? null : "price"))}
          testid={`${testidPrefix}-price`}
        />
        <Pill
          label="Possession"
          value={possessionLabel}
          active={activePop === "possession"}
          onClick={() => setActivePop((p) => (p === "possession" ? null : "possession"))}
          testid={`${testidPrefix}-possession`}
        />
        <Pill
          label="Amenities"
          value={csvLabel(filters.amenities)}
          active={activePop === "amenities"}
          onClick={() => setActivePop((p) => (p === "amenities" ? null : "amenities"))}
          testid={`${testidPrefix}-amenities`}
        />
        <Pill
          label="Sort"
          value={sortActive ? sortLabel : ""}
          icon={<ArrowUpDown size={11} />}
          active={activePop === "sort"}
          onClick={() => setActivePop((p) => (p === "sort" ? null : "sort"))}
          testid={`${testidPrefix}-sort`}
        />
        <button
          type="button"
          onClick={() => setPatch({ rera: !filters.rera })}
          aria-pressed={!!filters.rera}
          data-testid={`${testidPrefix}-rera`}
          className={`shrink-0 inline-flex items-center gap-1.5 h-10 px-4 rounded-full border text-[12.5px] font-semibold transition-all duration-200
                      ${filters.rera
                        ? "bg-[#15803D] text-white border-[#15803D] shadow-sm"
                        : "bg-white text-slate-700 border-slate-200 hover:border-[#15803D]/40 hover:bg-slate-50"}`}
        >
          <ShieldCheck size={13} /> RERA approved
        </button>
      </div>

      {/* ===== Popovers ===== */}
      {activePop === "location" && (
        <PillDetail title="Location" testid={`${testidPrefix}-pop-location`} onClose={closePop}>
          <LocationPicker
            options={locationOptions}
            value={filters.location || ""}
            currentInputDefault={filters.location || ""}
            onPick={(v) => { setPatch({ location: v }); closePop(); }}
            testidPrefix={`${testidPrefix}-location`}
          />
        </PillDetail>
      )}
      {activePop === "builder" && (
        <PillDetail title="Builder" testid={`${testidPrefix}-pop-builder`} onClose={closePop}>
          <MultiOptionList
            options={builderOptions.map((b) => ({ value: b.id, label: b.name }))}
            value={filters.builder_id || ""}
            onApply={(csv) => { setPatch({ builder_id: csv }); closePop(); }}
            testidPrefix={`${testidPrefix}-builder`}
            emptyText="No builders yet"
          />
        </PillDetail>
      )}
      {activePop === "bhk" && (
        <PillDetail title="BHK / Unit type" testid={`${testidPrefix}-pop-bhk`} onClose={closePop}>
          <MultiOptionGrid
            options={bhkOptions}
            value={filters.bhk || ""}
            onApply={(csv) => { setPatch({ bhk: csv }); closePop(); }}
            testidPrefix={`${testidPrefix}-bhk`}
            cols={3}
            emptyText="No BHK configurations available"
          />
        </PillDetail>
      )}
      {activePop === "price" && (
        <PillDetail title="Starting price" testid={`${testidPrefix}-pop-price`} onClose={closePop}>
          <PriceEditor
            value={filters.priceRange || [0, 999999999]}
            onApply={(range) => { setPatch({ priceRange: range }); closePop(); }}
            testidPrefix={`${testidPrefix}-price`}
          />
        </PillDetail>
      )}
      {activePop === "possession" && (
        <PillDetail title="Possession window" testid={`${testidPrefix}-pop-possession`} onClose={closePop}>
          <OptionList
            options={possessionOptions}
            value={filters.possession_window || ""}
            onPick={(v) => { setPatch({ possession_window: v === filters.possession_window ? "" : v }); closePop(); }}
            testidPrefix={`${testidPrefix}-possession`}
            emptyText="No possession windows configured"
          />
        </PillDetail>
      )}
      {activePop === "amenities" && (
        <PillDetail title="Amenities" testid={`${testidPrefix}-pop-amenities`} onClose={closePop}>
          <MultiOptionGrid
            options={amenityOptions}
            value={filters.amenities || ""}
            onApply={(csv) => { setPatch({ amenities: csv }); closePop(); }}
            testidPrefix={`${testidPrefix}-amenity`}
            cols={1}
            emptyText="No amenities configured yet"
          />
        </PillDetail>
      )}
      {activePop === "sort" && (
        <PillDetail title="Sort by" testid={`${testidPrefix}-pop-sort`} onClose={closePop}>
          <OptionList
            options={SORT_OPTIONS}
            value={filters.sort || DEFAULT_SORT}
            onPick={(v) => { setPatch({ sort: v === DEFAULT_SORT ? "" : v }); closePop(); }}
            testidPrefix={`${testidPrefix}-sort`}
          />
        </PillDetail>
      )}
    </div>
  );
}

// ─── Building blocks ────────────────────────────────────────────────
function Pill({ label, value, active, onClick, testid, icon = null }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      aria-expanded={active}
      className={`shrink-0 inline-flex items-center gap-1.5 h-10 px-4 rounded-full border text-[12.5px] font-semibold transition-all duration-200
                  ${active
                    ? "bg-slate-900 text-white border-slate-900 shadow-sm"
                    : value
                      ? "bg-[#0055FF] text-white border-[#0055FF] shadow-sm"
                      : "bg-white text-slate-700 border-slate-200 hover:border-[#0055FF]/40 hover:bg-slate-50"}`}
    >
      {icon}
      <span>{value || label}</span>
      <ChevronDown size={12} className={`transition-transform duration-200 ${active ? "rotate-180" : ""}`} />
    </button>
  );
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 767px)").matches;
  });
  useEffect(() => {
    const mql = window.matchMedia("(max-width: 767px)");
    const h = (e) => setIsMobile(e.matches);
    mql.addEventListener("change", h);
    return () => mql.removeEventListener("change", h);
  }, []);
  return isMobile;
}

function PillDetail({ children, testid, title, onClose }) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(true);
  const closeTimerRef = useRef(null);
  useEffect(() => () => closeTimerRef.current && clearTimeout(closeTimerRef.current), []);
  const closeAll = () => {
    setOpen(false);
    closeTimerRef.current = setTimeout(() => onClose?.(), 220);
  };
  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={(v) => { if (!v) closeAll(); }}>
        <SheetContent side="bottom" className="p-0 max-h-[80dvh] rounded-t-2xl flex flex-col" data-testid={testid}>
          <SheetHeader className="px-4 py-3 border-b">
            <div className="flex items-center justify-between">
              <SheetTitle className="text-[14.5px] font-semibold">{title}</SheetTitle>
              <button type="button" onClick={closeAll} aria-label="Close" className="text-slate-500 hover:text-slate-900">
                <X size={18} />
              </button>
            </div>
            <SheetDescription className="sr-only">Pick a {title} option.</SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto">{children}</div>
        </SheetContent>
      </Sheet>
    );
  }
  return (
    <div
      data-testid={testid}
      className="absolute top-full mt-2 left-0 z-50
                 bg-white rounded-2xl border border-slate-100 shadow-[0_12px_40px_-8px_rgba(15,23,42,0.18),0_4px_10px_-2px_rgba(15,23,42,0.08)]
                 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200 w-[320px]"
    >
      {children}
    </div>
  );
}

function OptionGrid({ options, value, onPick, testidPrefix, cols = 2, emptyText = "Nothing configured" }) {
  const gridClass = cols === 1 ? "grid-cols-1" : cols === 3 ? "grid-cols-3" : "grid-cols-2";
  return (
    <div className={`p-3 grid ${gridClass} gap-2 max-h-[60vh] overflow-y-auto`}>
      {options.length === 0 ? (
        <div className="col-span-full text-center text-[12px] text-slate-500 py-6">{emptyText}</div>
      ) : options.map((t) => {
        const active = value === t;
        return (
          <button
            key={t}
            onClick={() => onPick(t)}
            className={`text-[12.5px] font-semibold py-2.5 rounded-xl border transition-all inline-flex items-center justify-center gap-1.5
                        ${active
                          ? "bg-[#0055FF] text-white border-[#0055FF]"
                          : "bg-white text-slate-700 border-slate-200 hover:border-[#0055FF]/40 hover:bg-slate-50"}`}
            data-testid={`${testidPrefix}-opt-${String(t).replace(/[\s/]+/g, "-")}`}
          >
            {active && <Check size={12} />}
            {t}
          </button>
        );
      })}
    </div>
  );
}

// v2.87 Phase 3a — Multi-select staged grid (BHK).
function MultiOptionGrid({ options, value, onApply, testidPrefix, cols = 2, emptyText = "Nothing configured" }) {
  const initial = csvToArr(value);
  const [staged, setStaged] = useState(initial);
  useEffect(() => { setStaged(csvToArr(value)); /* eslint-disable-line */ }, [value]);
  const gridClass = cols === 1 ? "grid-cols-1" : cols === 3 ? "grid-cols-3" : "grid-cols-2";
  const toggle = (t) => setStaged((cur) => cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]);
  const dirty = staged.join(",") !== initial.join(",");
  return (
    <div className="flex flex-col" data-testid={`${testidPrefix}-multi`}>
      <div className={`p-3 grid ${gridClass} gap-2 overflow-y-auto`} style={{ maxHeight: "min(60vh, 400px)" }}>
        {options.length === 0 ? (
          <div className="col-span-full text-center text-[12px] text-slate-500 py-6">{emptyText}</div>
        ) : options.map((t) => {
          const active = staged.includes(t);
          return (
            <button
              key={t}
              type="button"
              onClick={() => toggle(t)}
              className={`text-[12.5px] font-semibold py-2.5 rounded-xl border transition-all inline-flex items-center justify-center gap-1.5
                          ${active
                            ? "bg-[#0055FF] text-white border-[#0055FF]"
                            : "bg-white text-slate-700 border-slate-200 hover:border-[#0055FF]/40 hover:bg-slate-50"}`}
              data-testid={`${testidPrefix}-opt-${String(t).replace(/[\s/]+/g, "-")}`}
              aria-pressed={active}
            >
              {active && <Check size={12} />}
              {t}
            </button>
          );
        })}
      </div>
      <div className="border-t border-slate-100 bg-white px-3 py-2 flex items-center gap-2 shrink-0">
        <button
          type="button" onClick={() => setStaged([])} disabled={staged.length === 0}
          className="text-[12px] font-semibold text-slate-600 hover:text-slate-900 disabled:opacity-40 px-2.5 py-1.5"
          data-testid={`${testidPrefix}-reset`}
        >Reset</button>
        <Button
          size="sm"
          onClick={() => onApply(arrToCsv(staged))}
          disabled={!dirty}
          className="ml-auto h-8 px-4 bg-[#0055FF] hover:bg-[#0044CC]"
          data-testid={`${testidPrefix}-apply`}
        >
          Apply{staged.length > 0 && ` (${staged.length})`}
        </Button>
      </div>
    </div>
  );
}

function OptionList({ options, value, onPick, testidPrefix, emptyText = "No options" }) {
  return (
    <div className="p-2 max-h-[60vh] overflow-y-auto">
      {options.length === 0 ? (
        <div className="text-center text-[12px] text-slate-500 py-6">{emptyText}</div>
      ) : options.map((o) => {
        const active = value === o.value;
        return (
          <button
            key={o.value || "_empty"}
            onClick={() => onPick(o.value)}
            className={`w-full text-left px-3 py-2.5 rounded-xl border transition-all text-[13px] font-semibold mb-1.5 inline-flex items-center justify-between
                        ${active
                          ? "bg-[#0055FF] text-white border-[#0055FF]"
                          : "bg-white text-slate-700 border-slate-200 hover:border-[#0055FF]/40 hover:bg-slate-50"}`}
            data-testid={`${testidPrefix}-opt-${String(o.value || "all").replace(/[\s:+]+/g, "-")}`}
          >
            <span>{o.label}</span>
            {active && <Check size={13} />}
          </button>
        );
      })}
    </div>
  );
}

// v2.87 Phase 3a — Multi-select staged list with Apply.
function MultiOptionList({ options, value, onApply, testidPrefix, emptyText = "No options" }) {
  const initial = csvToArr(value);
  const [staged, setStaged] = useState(initial);
  useEffect(() => { setStaged(csvToArr(value)); /* eslint-disable-line */ }, [value]);
  const toggle = (v) => setStaged((cur) => cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]);
  const dirty = staged.join(",") !== initial.join(",");
  return (
    <div className="flex flex-col" data-testid={`${testidPrefix}-multi`}>
      <div className="p-2 overflow-y-auto" style={{ maxHeight: "min(60vh, 400px)" }}>
        {options.length === 0 ? (
          <div className="text-center text-[12px] text-slate-500 py-6">{emptyText}</div>
        ) : options.map((o) => {
          const active = staged.includes(o.value);
          return (
            <button
              key={o.value || "_empty"}
              type="button"
              onClick={() => toggle(o.value)}
              className={`w-full text-left px-3 py-2.5 rounded-xl border transition-all text-[13px] font-semibold mb-1.5 inline-flex items-center justify-between
                          ${active
                            ? "bg-[#0055FF] text-white border-[#0055FF]"
                            : "bg-white text-slate-700 border-slate-200 hover:border-[#0055FF]/40 hover:bg-slate-50"}`}
              data-testid={`${testidPrefix}-opt-${String(o.value || "all").replace(/[\s:+]+/g, "-")}`}
              aria-pressed={active}
            >
              <span>{o.label}</span>
              {active && <Check size={13} />}
            </button>
          );
        })}
      </div>
      <div className="border-t border-slate-100 bg-white px-3 py-2 flex items-center gap-2 shrink-0">
        <button
          type="button" onClick={() => setStaged([])} disabled={staged.length === 0}
          className="text-[12px] font-semibold text-slate-600 hover:text-slate-900 disabled:opacity-40 px-2.5 py-1.5"
          data-testid={`${testidPrefix}-reset`}
        >Reset</button>
        <Button
          size="sm" onClick={() => onApply(arrToCsv(staged))} disabled={!dirty}
          className="ml-auto h-8 px-4 bg-[#0055FF] hover:bg-[#0044CC]"
          data-testid={`${testidPrefix}-apply`}
        >Apply{staged.length > 0 && ` (${staged.length})`}</Button>
      </div>
    </div>
  );
}

function PriceEditor({ value, onApply, testidPrefix }) {
  const [minVal, setMinVal] = useState(value[0] || "");
  const [maxVal, setMaxVal] = useState(value[1] >= 999999999 ? "" : value[1] || "");
  return (
    <div className="p-4 space-y-3">
      <div className="space-y-1.5">
        {PRICE_PRESETS.map((p) => (
          <button
            key={p.label}
            onClick={() => onApply(p.range)}
            className="w-full text-left px-3 py-2.5 rounded-xl border border-slate-200 hover:border-[#0055FF]/40 hover:bg-slate-50 text-[13px] font-semibold transition-all"
            data-testid={`${testidPrefix}-preset-${p.label.replace(/[\s+/]+/g, "-")}`}
          >
            {p.label}
          </button>
        ))}
      </div>
      <div className="border-t border-slate-100 pt-3">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-2">Or custom</div>
        <div className="grid grid-cols-2 gap-2">
          <Input type="number" value={minVal} onChange={(e) => setMinVal(e.target.value)} placeholder="Min" className="h-9 text-[13px]" data-testid={`${testidPrefix}-min`} />
          <Input type="number" value={maxVal} onChange={(e) => setMaxVal(e.target.value)} placeholder="Max" className="h-9 text-[13px]" data-testid={`${testidPrefix}-max`} />
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onApply([Number(minVal) || 0, Number(maxVal) || 999999999])}
          disabled={!minVal && !maxVal}
          className="w-full mt-2 h-9"
          data-testid={`${testidPrefix}-apply-custom`}
        >
          Apply
        </Button>
      </div>
    </div>
  );
}

function LocationPicker({ options, value, onPick, testidPrefix }) {
  // Free-text input that lets the user pick from suggestions OR type any
  // substring. Submit-on-Enter writes the typed value into filters.location.
  const [draft, setDraft] = useState(value || "");
  const filtered = (options || []).filter((o) => !draft || o.toLowerCase().includes(draft.toLowerCase())).slice(0, 30);
  return (
    <div className="p-3">
      <div className="relative mb-2">
        <Input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Type any location…"
          className="h-10 text-[13px] pr-20"
          data-testid={`${testidPrefix}-input`}
          onKeyDown={(e) => { if (e.key === "Enter") onPick(draft.trim()); }}
        />
        <Button
          size="sm"
          onClick={() => onPick(draft.trim())}
          disabled={!draft.trim()}
          className="absolute right-1.5 top-1.5 h-7 bg-[#0055FF] hover:bg-[#0044CC]"
          data-testid={`${testidPrefix}-apply`}
        >
          Apply
        </Button>
      </div>
      {value && (
        <button
          onClick={() => onPick("")}
          className="w-full text-left px-3 py-2 text-[12px] text-slate-500 hover:text-slate-900 mb-1.5"
          data-testid={`${testidPrefix}-clear`}
        >
          Clear &quot;{value}&quot;
        </button>
      )}
      <div className="max-h-[40vh] overflow-y-auto space-y-1">
        {filtered.length === 0 && draft && (
          <div className="text-[12px] text-slate-500 italic px-2">Press Apply to use &quot;{draft}&quot;</div>
        )}
        {filtered.map((opt) => (
          <button
            key={opt}
            onClick={() => onPick(opt)}
            className={`w-full text-left px-3 py-2 rounded-lg text-[12.5px] font-medium transition-all
                        ${value === opt ? "bg-[#0055FF] text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
            data-testid={`${testidPrefix}-opt-${opt.replace(/[\s/]+/g, "-")}`}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}
